/**
 * The review list for the `proposal:cluster` output kind on the generic agent
 * report page: every group this agent proposed that waits for a person, with
 * accept (the items become an unresolved cluster to resolve) and dismiss, the
 * link to resolve the cluster just accepted, and the recently reviewed groups
 * with Reopen for whoever reviewed them. Accept and dismiss go through the same
 * store functions every other Twins surface uses, so the triggers that enforce
 * reviewers and stamp decision records fire the same way.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Callout } from '../../ds';
import { useTwinsStore } from '../../../stores/prioritization/twinsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { ReverseActionButton } from '../../reversals/ReverseActionButton';
import { reopenTwinsCluster } from '../../../lib/reversals/reversalsService';
import { when, type OutputReviewProps } from './types';
import { useOpenClusterProposals } from './useOpenClusterProposals';

export function ClusterProposalsReview({ def, workspaceId, mayReview, onChanged, titleOf }: OutputReviewProps) {
  const userId = useUserStore((s) => s.userId);
  const accept = useTwinsStore((s) => s.accept);
  const dismiss = useTwinsStore((s) => s.dismiss);
  const history = useTwinsStore((s) => s.history);
  const fetchHistory = useTwinsStore((s) => s.fetchHistory);
  const fetchClusters = useTwinsStore((s) => s.fetchClusters);
  const open = useOpenClusterProposals(def.agentType);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acceptedGroupId, setAcceptedGroupId] = useState<string | null>(null);

  useEffect(() => { void fetchHistory(workspaceId, 50); }, [workspaceId, fetchHistory]);
  const reviewed = useMemo(() => history.filter((c) => c.proposedByAgent === def.agentType).slice(0, 10), [history, def.agentType]);

  const run = async (id: string, act: () => Promise<{ success: boolean; error?: string; relationshipGroupId?: string | null }>) => {
    setBusyId(id);
    setError(null);
    const result = await act();
    if (!result.success) setError(result.error ?? 'Not saved');
    else if (result.relationshipGroupId) setAcceptedGroupId(result.relationshipGroupId);
    onChanged();
    setBusyId(null);
  };

  const reload = () => { void fetchClusters(workspaceId); void fetchHistory(workspaceId, 50); onChanged(); };

  return (
    <div className="space-y-2">
      {error && <Callout tone="coral">{error}</Callout>}
      {acceptedGroupId && (
        <Callout tone="neutral">
          Accepted. These items are now an unresolved cluster — decide whether they are duplicates, variants or a reframe.{' '}
          <Link to={`/prioritization/relationships/clusters/${acceptedGroupId}`} className="underline">Resolve the cluster</Link>
        </Callout>
      )}
      {!mayReview && open.length > 0 && (
        <Callout tone="neutral">Reviewing what {def.name} proposes needs one of its review roles. A workspace administrator can grant one.</Callout>
      )}
      {open.length === 0 ? (
        <div className="card text-center py-10">
          <Sparkles className="h-6 w-6 mx-auto text-[var(--text-ter)]" />
          <p className="text-sm font-medium text-[var(--text-pri)] mt-2">Nothing waiting</p>
          <p className="text-xs text-[var(--text-ter)] mt-1">Run {def.name} over a scope to look for duplicates and variants.</p>
        </div>
      ) : open.map((c) => (
        <div key={c.id} className="card space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)] capitalize">
              {c.clusterType}
            </span>
            <span className="text-xs text-[var(--text-ter)]">{c.itemIds.length} items</span>
            <span className="ml-auto text-xs text-[var(--text-ter)]">{when(c.createdAt)}</span>
          </div>
          <ul className="space-y-0.5">
            {c.itemIds.map((id) => (
              <li key={id}><Link to={`/prioritization/items/${id}`} className="text-sm text-[var(--text-pri)] underline">{titleOf(id)}</Link></li>
            ))}
          </ul>
          {c.rationale && <p className="text-xs text-[var(--text-ter)] italic">"{c.rationale}"</p>}
          {mayReview && userId && (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" disabled={busyId === c.id} onClick={() => void run(c.id, () => dismiss(c.id, userId))}>
                <X className="h-3.5 w-3.5" /> Dismiss
              </Button>
              <Button variant="primary" size="sm" disabled={busyId === c.id} onClick={() => void run(c.id, () => accept(c.id, userId))}>
                {busyId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Accept
              </Button>
            </div>
          )}
        </div>
      ))}
      {reviewed.length > 0 && (
        <div className="pt-3">
          <p className="text-xs font-medium text-[var(--text-sec)] mb-1">Recently reviewed</p>
          <div className="divide-y divide-[var(--rule)]">
            {reviewed.map((c) => (
              <div key={c.id} className="py-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-[var(--text-sec)]">{c.status === 'accepted' ? 'Accepted' : 'Dismissed'}</span>
                <span className="text-[var(--text-pri)]">{c.itemIds.map(titleOf).join(' · ')}</span>
                <span className="ml-auto text-[var(--text-ter)]">{when(c.reviewedAt ?? c.createdAt)}</span>
                {userId && c.reviewedByUserId === userId && (
                  <ReverseActionButton
                    label="Reopen"
                    confirmTitle="Reopen this group"
                    explain="This returns the group to the waiting list for review again."
                    run={(reason) => reopenTwinsCluster(c.id, reason)}
                    onDone={reload}
                    variant="ghost"
                    size="sm"
                    className="!px-2 !py-1 !text-xs"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
